import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Alert, Tabs, Tab,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4001';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
function buildYears() { const y = new Date().getFullYear(); return [y - 2, y - 1, y, y + 1]; }

const fc = (v: number | null | undefined, dec = 0) =>
  v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });

// ── Colors ────────────────────────────────────────────────────────────────────
const NAVY = '#1E2D78';
const GREEN = '#2E7D32';
const TEAL = '#00897B';
const ORANGE = '#E65100';
const BLUE_BAR = '#4472C4';
const GREEN_BAR = '#70AD47';
const ORANGE_BAR = '#ED7D31';
const RED_BAR = '#FF0000';
const YELLOW_BAR = '#FFC000';
const GREY_BAR = '#A5A5A5';

// ── Cell styles ───────────────────────────────────────────────────────────────
const TH: React.CSSProperties = {
  background: NAVY, color: '#fff', fontWeight: 700,
  border: '1px solid #0d1b57', padding: '6px 10px', fontSize: '10px',
  textAlign: 'center', whiteSpace: 'nowrap', lineHeight: 1.3,
};
const TD: React.CSSProperties = {
  border: '1px solid #ccc', padding: '5px 10px', fontSize: '10px',
  textAlign: 'center', whiteSpace: 'nowrap', verticalAlign: 'middle',
};
const TH_LEFT: React.CSSProperties = { ...TH, textAlign: 'left' };
const TD_LEFT: React.CSSProperties = { ...TD, textAlign: 'left' };
const TD_RIGHT: React.CSSProperties = { ...TD, textAlign: 'right' };
const TD_BOLD: React.CSSProperties = { ...TD, fontWeight: 700 };

// ── Page wrapper for print ────────────────────────────────────────────────────
const ReportPage: React.FC<{ children: React.ReactNode; landscape?: boolean; noPad?: boolean }> = ({ children, landscape, noPad }) => (
  <Paper
    elevation={2}
    className={landscape ? 'report-page landscape-page' : 'report-page portrait-page'}
    sx={{
      mb: 3, overflow: 'hidden', p: noPad ? 0 : '14mm',
      '@media print': { boxShadow: 'none', border: 'none', mb: 0 },
    }}
  >
    {children}
  </Paper>
);

// ── Header bar (top of each slide) ───────────────────────────────────────────
const SlideHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Box sx={{
    background: `linear-gradient(135deg, ${NAVY} 0%, #2a3f9e 100%)`,
    color: '#fff', p: '12px 20px', mb: 2, borderRadius: '4px',
  }}>
    <Typography sx={{ fontWeight: 800, fontSize: '14px', letterSpacing: 0.5 }}>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: '10px', opacity: 0.8 }}>{subtitle}</Typography>}
  </Box>
);

// ── SCA Summary Row ──────────────────────────────────────────────────────────
const ScaSummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography sx={{ fontSize: '10px', color: '#555' }}>{label}</Typography>
    <Typography sx={{ fontSize: '10px', fontWeight: 700 }}>{value}</Typography>
  </Box>
);

// ══════════════════════════════════════════════════════════════════════════════
// SLIDE 1: Cover Page
// ══════════════════════════════════════════════════════════════════════════════
const CoverPage: React.FC<{ data: any }> = ({ data }) => (
  <ReportPage>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 500 }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #2a3f9e 100%)`,
        color: '#fff', p: 6, borderRadius: '8px', textAlign: 'center', width: '80%',
      }}>
        <Typography sx={{ fontSize: '28px', fontWeight: 800, mb: 1, letterSpacing: 1 }}>
          {data.title}
        </Typography>
        <Box sx={{ width: 80, height: 3, bgcolor: '#FFC000', mx: 'auto', my: 2.5 }} />
        <Typography sx={{ fontSize: '16px', fontWeight: 600, mb: 1 }}>
          {data.subtitle}
        </Typography>
        <Typography sx={{ fontSize: '12px', opacity: 0.8 }}>
          SURALLAH BIOGAS VENTURES CORP.
        </Typography>
        <Typography sx={{ fontSize: '12px', opacity: 0.8, mt: 1 }}>
          Report Period: {data.dateRange}
        </Typography>
      </Box>
      <Box sx={{ mt: 4, display: 'flex', gap: 4, alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '10px', color: '#666' }}>Powered by</Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: NAVY }}>METPower</Typography>
        </Box>
      </Box>
    </Box>
  </ReportPage>
);

// ══════════════════════════════════════════════════════════════════════════════
// SLIDE 2: Substrate Supply
// ══════════════════════════════════════════════════════════════════════════════
const SubstrateSupplyPage: React.FC<{ data: any; reportData: any }> = ({ data, reportData }) => {
  const chartData = (data.weeks || []).map((w: any) => ({
    name: w.label,
    delivered: Math.round(w.delivered),
    diverted: Math.round(w.diverted),
    cumulativeTarget: w.cumulativeTarget,
  }));

  const cyDeliveryPct = data.annualScaMin > 0
    ? ((data.ytdTotal / data.annualScaMin) * 100).toFixed(1)
    : '—';

  return (
    <ReportPage landscape>
      <SlideHeader title="Substrate Supply" subtitle={reportData.dateRange} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 3 }}>
        {/* Left panel - SCA summary */}
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1.5, color: NAVY }}>SCA Parameters</Typography>
          <ScaSummaryRow label="Annual SCA Min:" value={`${fc(data.annualScaMin)} MT`} />
          <ScaSummaryRow label="Monthly SCA Min:" value={`${fc(data.monthlyScaMin)} MT`} />
          <Box sx={{ my: 1.5, borderBottom: '1px solid #ddd' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1, color: GREEN }}>This Month</Typography>
          <ScaSummaryRow label="Delivered:" value={`${fc(Math.round(data.delivered))} MT`} />
          <ScaSummaryRow label="Diverted/Rejected:" value={`${fc(Math.round(data.diverted + data.rejected))} MT`} />
          <Box sx={{ my: 1.5, borderBottom: '1px solid #ddd' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1, color: TEAL }}>Year-to-Date</Typography>
          <ScaSummaryRow label="YTD Delivered:" value={`${fc(Math.round(data.ytdTotal))} MT`} />
          <ScaSummaryRow label="CY Delivery %:" value={`${cyDeliveryPct}%`} />
        </Box>

        {/* Right panel - Chart */}
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1, color: NAVY, textAlign: 'center' }}>
            Weekly Substrate Delivery (MT)
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip contentStyle={{ fontSize: 10 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="delivered" fill={GREEN_BAR} name="Delivered (MT)" stackId="a" />
              <Bar dataKey="diverted" fill={GREY_BAR} name="Diverted (MT)" stackId="a" />
              <Line type="monotone" dataKey="cumulativeTarget" stroke={RED_BAR} strokeWidth={2} name="Cumulative Target" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Weekly breakdown table */}
      <Box sx={{ mt: 2 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={TH_LEFT}>Week</th>
              <th style={TH}>Delivered (MT)</th>
              <th style={TH}>Diverted (MT)</th>
              <th style={TH}>Cum. Target (MT)</th>
            </tr>
          </thead>
          <tbody>
            {(data.weeks || []).map((w: any, i: number) => (
              <tr key={i}>
                <td style={TD_LEFT}>{w.label}</td>
                <td style={TD}>{fc(Math.round(w.delivered))}</td>
                <td style={TD}>{fc(Math.round(w.diverted))}</td>
                <td style={TD}>{fc(w.cumulativeTarget)}</td>
              </tr>
            ))}
            <tr style={{ background: '#f0f4ff' }}>
              <td style={{ ...TD_LEFT, fontWeight: 700 }}>Total</td>
              <td style={TD_BOLD}>{fc(Math.round(data.delivered))}</td>
              <td style={TD_BOLD}>{fc(Math.round(data.diverted))}</td>
              <td style={TD_BOLD}>{fc(data.monthlyScaMin)}</td>
            </tr>
          </tbody>
        </table>
      </Box>
    </ReportPage>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SLIDE 3: Biogas Conversion Yield - Weekly
// ══════════════════════════════════════════════════════════════════════════════
const BiogasConversionWeeklyPage: React.FC<{ data: any; reportData: any }> = ({ data, reportData }) => {
  const chartData = (data.weeks || []).map((w: any) => ({
    name: w.label,
    actualYield: w.actualYieldNm3PerTon,
    targetYield: w.targetYield,
    pctDS: w.pctDS,
  }));

  return (
    <ReportPage landscape>
      <SlideHeader title="Biogas Conversion Yield - Weekly" subtitle={reportData.dateRange} />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 9 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 9 }} label={{ value: 'Nm³/ton', angle: -90, position: 'insideLeft', style: { fontSize: 9 } }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9 }} label={{ value: '%DS', angle: 90, position: 'insideRight', style: { fontSize: 9 } }} />
            <Tooltip contentStyle={{ fontSize: 10 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar yAxisId="left" dataKey="actualYield" fill={BLUE_BAR} name="Actual Yield (Nm³/ton)" />
            <Line yAxisId="left" type="monotone" dataKey="targetYield" stroke={RED_BAR} strokeWidth={2} name="Target Yield" dot={false} />
            <Line yAxisId="right" type="monotone" dataKey="pctDS" stroke={ORANGE_BAR} strokeWidth={2} name="%DS" dot />
          </ComposedChart>
        </ResponsiveContainer>

        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={TH_LEFT}>Week</th>
              <th style={TH}>Actual Gas (Nm³)</th>
              <th style={TH}>SCA Equivalent (Nm³)</th>
              <th style={TH}>Actual Yield (Nm³/ton)</th>
              <th style={TH}>Target Yield (Nm³/ton)</th>
              <th style={TH}>%DS</th>
            </tr>
          </thead>
          <tbody>
            {(data.weeks || []).map((w: any, i: number) => (
              <tr key={i}>
                <td style={TD_LEFT}>{w.label}</td>
                <td style={TD}>{fc(w.actualGasNm3)}</td>
                <td style={TD}>{fc(w.scaGasEquivalentNm3)}</td>
                <td style={TD}>{fc(w.actualYieldNm3PerTon)}</td>
                <td style={TD}>{fc(w.targetYield)}</td>
                <td style={TD}>{w.pctDS > 0 ? w.pctDS.toFixed(1) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </ReportPage>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SLIDE 4: Biogas Conversion Yield - Monthly / YTD
// ══════════════════════════════════════════════════════════════════════════════
const BiogasConversionMonthlyPage: React.FC<{ data: any; reportData: any }> = ({ data, reportData }) => {
  const chartData = (data.monthlyYield || []).map((m: any) => ({
    name: m.month,
    actualYield: m.actualYield,
    ytd2025: m.ytd2025,
    targetYield: m.targetYield,
  }));

  return (
    <ReportPage landscape>
      <SlideHeader title="Biogas Conversion Yield - Monthly vs YTD" subtitle={`${reportData.year} Calendar Year`} />

      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 9 }} />
          <YAxis tick={{ fontSize: 9 }} label={{ value: 'Nm³/ton', angle: -90, position: 'insideLeft', style: { fontSize: 9 } }} />
          <Tooltip contentStyle={{ fontSize: 10 }} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <Bar dataKey="actualYield" fill={BLUE_BAR} name="Actual Yield Monthly" />
          <Line type="monotone" dataKey="ytd2025" stroke={GREEN_BAR} strokeWidth={2} name={`YTD ${reportData.year}`} dot />
          <ReferenceLine y={101} stroke={RED_BAR} strokeWidth={2} strokeDasharray="5 5" label={{ value: 'Target: 101', position: 'right', fill: RED_BAR, fontSize: 9 }} />
        </ComposedChart>
      </ResponsiveContainer>

      <Box sx={{ mt: 2, overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={TH_LEFT}>Month</th>
              <th style={TH}>Actual Yield</th>
              <th style={TH}>YTD {reportData.year}</th>
              <th style={TH}>YTD CY</th>
              <th style={TH}>Target</th>
            </tr>
          </thead>
          <tbody>
            {(data.monthlyYield || []).map((m: any, i: number) => (
              <tr key={i} style={{ background: m.actualYield > 0 ? 'transparent' : '#f9f9f9' }}>
                <td style={TD_LEFT}>{m.month}</td>
                <td style={TD}>{m.actualYield > 0 ? fc(m.actualYield) : '—'}</td>
                <td style={TD}>{m.ytd2025 > 0 ? fc(m.ytd2025) : '—'}</td>
                <td style={TD}>{m.ytdCY > 0 ? fc(m.ytdCY) : '—'}</td>
                <td style={TD}>{fc(m.targetYield)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </ReportPage>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SLIDE 5: Biogas Utilization
// ══════════════════════════════════════════════════════════════════════════════
const BiogasUtilizationPage: React.FC<{ data: any; reportData: any }> = ({ data, reportData }) => {
  const chartData = (data.weeks || []).map((w: any) => ({
    name: w.label,
    deliveredToBoilers: w.deliveredToBoilers,
    flared: w.flared,
    co2Operation: w.co2Operation,
    cumulativeTarget: w.cumulativeTarget,
  }));

  const cyDeliveryPct = data.annualScaMin > 0
    ? ((data.ytdTotal / data.annualScaMin) * 100).toFixed(1)
    : '—';

  return (
    <ReportPage landscape>
      <SlideHeader title="Biogas Utilization" subtitle={reportData.dateRange} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 3 }}>
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1.5, color: NAVY }}>SCA Parameters</Typography>
          <ScaSummaryRow label="Annual SCA Min:" value={`${fc(data.annualScaMin)} Nm³`} />
          <ScaSummaryRow label="Monthly SCA Min:" value={`${fc(data.monthlyScaMin)} Nm³`} />
          <Box sx={{ my: 1.5, borderBottom: '1px solid #ddd' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1, color: GREEN }}>This Month</Typography>
          <ScaSummaryRow label="Total Delivered:" value={`${fc(Math.round(data.delivered))} Nm³`} />
          <Box sx={{ my: 1.5, borderBottom: '1px solid #ddd' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1, color: TEAL }}>Year-to-Date</Typography>
          <ScaSummaryRow label="YTD Delivered:" value={`${fc(Math.round(data.ytdTotal))} Nm³`} />
          <ScaSummaryRow label="Target to Reach:" value={`${fc(data.targetToReach)} Nm³`} />
          <ScaSummaryRow label="CY Delivery %:" value={`${cyDeliveryPct}%`} />
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1, color: NAVY, textAlign: 'center' }}>
            Weekly Biogas Utilization (Nm³)
          </Typography>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip contentStyle={{ fontSize: 10 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="deliveredToBoilers" fill={BLUE_BAR} name="Delivered to Boilers" stackId="a" />
              <Bar dataKey="flared" fill={ORANGE_BAR} name="Flared" stackId="a" />
              <Bar dataKey="co2Operation" fill={GREY_BAR} name="CO₂ Operation" stackId="a" />
              <Line type="monotone" dataKey="cumulativeTarget" stroke={RED_BAR} strokeWidth={2} name="Cumulative Target" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={TH_LEFT}>Week</th>
              <th style={TH}>Delivered to Boilers (Nm³)</th>
              <th style={TH}>Flared (Nm³)</th>
              <th style={TH}>CO₂ Operation (Nm³)</th>
              <th style={TH}>Cum. Target (Nm³)</th>
            </tr>
          </thead>
          <tbody>
            {(data.weeks || []).map((w: any, i: number) => (
              <tr key={i}>
                <td style={TD_LEFT}>{w.label}</td>
                <td style={TD}>{fc(w.deliveredToBoilers)}</td>
                <td style={TD}>{fc(w.flared)}</td>
                <td style={TD}>{fc(w.co2Operation)}</td>
                <td style={TD}>{fc(w.cumulativeTarget)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </ReportPage>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SLIDE 6: Power Generation
// ══════════════════════════════════════════════════════════════════════════════
const PowerGenerationPage: React.FC<{ data: any; reportData: any }> = ({ data, reportData }) => {
  const chartData = (data.weeks || []).map((w: any) => ({
    name: w.label,
    delivered: w.delivered,
    cumulativeTarget: w.cumulativeTarget,
  }));

  const cyDeliveryPct = data.annualScaMin > 0
    ? ((data.ytdTotal / data.annualScaMin) * 100).toFixed(1)
    : '—';

  return (
    <ReportPage landscape>
      <SlideHeader title="Power Generation" subtitle={reportData.dateRange} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 3 }}>
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1.5, color: NAVY }}>SCA Parameters</Typography>
          <ScaSummaryRow label="Annual SCA Min:" value={`${fc(data.annualScaMin)} kWh`} />
          <ScaSummaryRow label="Monthly SCA Min:" value={`${fc(data.monthlyScaMin)} kWh`} />
          <Box sx={{ my: 1.5, borderBottom: '1px solid #ddd' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1, color: GREEN }}>This Month</Typography>
          <ScaSummaryRow label="Delivered:" value={`${fc(Math.round(data.delivered))} kWh`} />
          <Box sx={{ my: 1.5, borderBottom: '1px solid #ddd' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1, color: TEAL }}>Year-to-Date</Typography>
          <ScaSummaryRow label="YTD Delivered:" value={`${fc(Math.round(data.ytdTotal))} kWh`} />
          <ScaSummaryRow label="Target to Reach:" value={`${fc(data.targetToReach)} kWh`} />
          <ScaSummaryRow label="CY Delivery %:" value={`${cyDeliveryPct}%`} />
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1, color: NAVY, textAlign: 'center' }}>
            Weekly Power Generation (kWh)
          </Typography>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip contentStyle={{ fontSize: 10 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="delivered" fill={YELLOW_BAR} name="Delivered (kWh)" />
              <Line type="monotone" dataKey="cumulativeTarget" stroke={RED_BAR} strokeWidth={2} name="Cumulative Target" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={TH_LEFT}>Week</th>
              <th style={TH}>Delivered (kWh)</th>
              <th style={TH}>Cum. Target (kWh)</th>
            </tr>
          </thead>
          <tbody>
            {(data.weeks || []).map((w: any, i: number) => (
              <tr key={i}>
                <td style={TD_LEFT}>{w.label}</td>
                <td style={TD}>{fc(w.delivered)}</td>
                <td style={TD}>{fc(w.cumulativeTarget)}</td>
              </tr>
            ))}
            <tr style={{ background: '#f0f4ff' }}>
              <td style={{ ...TD_LEFT, fontWeight: 700 }}>Total</td>
              <td style={TD_BOLD}>{fc(Math.round(data.delivered))}</td>
              <td style={TD_BOLD}>{fc(data.monthlyScaMin)}</td>
            </tr>
          </tbody>
        </table>
      </Box>
    </ReportPage>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SLIDE 7: Weekly Updates (Gas Quality & Fermenter Data)
// ══════════════════════════════════════════════════════════════════════════════
const WeeklyUpdatesPage: React.FC<{ data: any; reportData: any }> = ({ data, reportData }) => {
  const gq = data.gasQuality || { us: {}, ds: {} };
  const fermenters = data.fermenters || [];

  return (
    <ReportPage>
      <SlideHeader title="Weekly Updates" subtitle={reportData.dateRange} />

      {/* Operational Summary */}
      <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0', mb: 3 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '12px', mb: 1.5, color: NAVY }}>Operational Summary</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: '10px', color: '#666' }}>Canning Days</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{data.canningDays}/{data.totalDays} days</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '10px', color: '#666' }}>Fresh Mass Feeding Rate</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{data.freshMassFeeding || '—'}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '10px', color: '#666' }}>Avg. Gas Production</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{data.gasProductionAvg || '—'}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Gas Quality */}
      <Typography sx={{ fontWeight: 700, fontSize: '12px', mb: 1, color: NAVY }}>Gas Quality</Typography>
      <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 20 }}>
        <thead>
          <tr>
            <th style={TH_LEFT}>Parameter</th>
            <th style={TH}>Unit</th>
            <th style={TH}>Upstream (US)</th>
            <th style={TH}>Downstream (DS)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={TD_LEFT}>CH₄</td>
            <td style={TD}>%</td>
            <td style={TD}>{gq.us?.ch4 > 0 ? gq.us.ch4.toFixed(1) : '—'}</td>
            <td style={TD}>{gq.ds?.ch4 > 0 ? gq.ds.ch4.toFixed(1) : '—'}</td>
          </tr>
          <tr>
            <td style={TD_LEFT}>CO₂</td>
            <td style={TD}>%</td>
            <td style={TD}>{gq.us?.co2 > 0 ? gq.us.co2.toFixed(1) : '—'}</td>
            <td style={TD}>{gq.ds?.co2 > 0 ? gq.ds.co2.toFixed(1) : '—'}</td>
          </tr>
          <tr>
            <td style={TD_LEFT}>O₂</td>
            <td style={TD}>%</td>
            <td style={TD}>{gq.us?.o2 > 0 ? gq.us.o2.toFixed(2) : '—'}</td>
            <td style={TD}>{gq.ds?.o2 > 0 ? gq.ds.o2.toFixed(2) : '—'}</td>
          </tr>
          <tr>
            <td style={TD_LEFT}>H₂S</td>
            <td style={TD}>ppm</td>
            <td style={TD}>{gq.us?.h2s > 0 ? gq.us.h2s.toFixed(0) : '—'}</td>
            <td style={TD}>{gq.ds?.h2s > 0 ? gq.ds.h2s.toFixed(0) : '—'}</td>
          </tr>
        </tbody>
      </table>

      {/* Fermenter pH and FOS/TAC */}
      <Typography sx={{ fontWeight: 700, fontSize: '12px', mb: 1, color: NAVY }}>Fermenter Data</Typography>
      <table style={{ borderCollapse: 'collapse', width: '60%', marginBottom: 16 }}>
        <thead>
          <tr>
            <th style={TH_LEFT}>Fermenter</th>
            <th style={TH}>pH</th>
            <th style={TH}>FOS/TAC</th>
          </tr>
        </thead>
        <tbody>
          {fermenters.map((f: any, i: number) => (
            <tr key={i}>
              <td style={TD_LEFT}>{f.name}</td>
              <td style={TD}>{f.ph > 0 ? f.ph.toFixed(2) : '—'}</td>
              <td style={TD}>{f.fosTac > 0 ? f.fosTac.toFixed(2) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportPage>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SLIDE 8: Revenue Tracking
// ══════════════════════════════════════════════════════════════════════════════
const RevenueTrackingPage: React.FC<{ data: any; reportData: any }> = ({ data, reportData }) => {
  const months = data.months || [];

  return (
    <ReportPage landscape>
      <SlideHeader title="Revenue Tracking" subtitle={`${reportData.year} Calendar Year`} />

      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={TH_LEFT}>Month</th>
              <th style={TH}>Total Revenue (PHP)</th>
              <th style={TH}>LE (PHP)</th>
              <th style={TH}>Substrates Diff</th>
              <th style={TH}>Biogas Diff</th>
              <th style={TH}>Power Diff</th>
              <th style={TH}>Total Diff</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m: any, i: number) => (
              <tr key={i} style={{ background: m.totalRevenue > 0 ? 'transparent' : '#f9f9f9' }}>
                <td style={TD_LEFT}>{m.month}</td>
                <td style={TD_RIGHT}>{m.totalRevenue > 0 ? fc(m.totalRevenue) : '—'}</td>
                <td style={TD_RIGHT}>{m.le > 0 ? fc(m.le) : '—'}</td>
                <td style={TD_RIGHT}>{m.substratesDiff !== 0 ? fc(m.substratesDiff) : '—'}</td>
                <td style={TD_RIGHT}>{m.biogasDiff !== 0 ? fc(m.biogasDiff) : '—'}</td>
                <td style={TD_RIGHT}>{m.powerDiff !== 0 ? fc(m.powerDiff) : '—'}</td>
                <td style={{ ...TD_RIGHT, fontWeight: m.totalDiff !== 0 ? 700 : 400, color: m.totalDiff < 0 ? RED_BAR : 'inherit' }}>
                  {m.totalDiff !== 0 ? fc(m.totalDiff) : '—'}
                </td>
              </tr>
            ))}
            {data.grandTotal && (
              <tr style={{ background: '#f0f4ff' }}>
                <td style={{ ...TD_LEFT, fontWeight: 700 }}>Grand Total</td>
                <td style={{ ...TD_RIGHT, fontWeight: 700 }}>{fc(data.grandTotal.totalRevenue)}</td>
                <td style={{ ...TD_RIGHT, fontWeight: 700 }}>{fc(data.grandTotal.le)}</td>
                <td style={TD_RIGHT}>—</td>
                <td style={TD_RIGHT}>—</td>
                <td style={TD_RIGHT}>—</td>
                <td style={{ ...TD_RIGHT, fontWeight: 700, color: data.grandTotal.totalDiff < 0 ? RED_BAR : 'inherit' }}>
                  {fc(data.grandTotal.totalDiff)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      {/* Weekly revenue breakdown for current month */}
      {(data.weekBreakdown || []).length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '11px', mb: 1, color: NAVY }}>
            {MONTHS[reportData.month - 1]} Weekly Breakdown
          </Typography>
          <table style={{ borderCollapse: 'collapse', width: '60%' }}>
            <thead>
              <tr>
                <th style={TH_LEFT}>Week</th>
                <th style={TH}>Revenue (PHP)</th>
              </tr>
            </thead>
            <tbody>
              {(data.weekBreakdown || []).map((w: any, i: number) => (
                <tr key={i}>
                  <td style={TD_LEFT}>{w.label}</td>
                  <td style={TD_RIGHT}>{fc(w.totalRevenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </ReportPage>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const WeeklyMonthlyReport: React.FC = () => {
  const navigate = useNavigate();
  const today = new Date();
  const [repYear, setRepYear] = useState(today.getFullYear());
  const [repMonth, setRepMonth] = useState(today.getMonth() + 1);
  const [repWeek, setRepWeek] = useState<number | ''>('');
  const [reportType, setReportType] = useState<'monthly' | 'weekly'>('monthly');
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSaveMsg(null);
    try {
      const params: any = { year: repYear, month: repMonth };
      if (reportType === 'weekly' && repWeek) params.week = repWeek;
      const res = await axios.get(`${API}/weekly-report/generate`, { params });
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  }, [repYear, repMonth, repWeek, reportType]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await axios.post(`${API}/weekly-report/save`, {
        year: repYear,
        month: repMonth,
        week: reportType === 'weekly' && repWeek ? repWeek : undefined,
      });
      setSaveMsg('Report saved successfully!');
    } catch (err: any) {
      setSaveMsg('Failed to save: ' + (err?.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const weekOptions = data?.weeks || [];

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Controls (screen only) ── */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', '@media print': { display: 'none' } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small" variant="outlined">Back</Button>
        <AssessmentIcon sx={{ color: '#1b5e20', fontSize: 28 }} />
        <Typography variant="h6" fontWeight={700} color="#1b5e20">
          {reportType === 'weekly' ? 'Weekly Report' : 'Monthly Report'}
        </Typography>
        <Box sx={{ flex: 1 }} />

        <Tabs
          value={reportType}
          onChange={(_, v) => { setReportType(v); setRepWeek(''); }}
          sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}
        >
          <Tab label="Monthly" value="monthly" />
          <Tab label="Weekly" value="weekly" />
        </Tabs>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Month</InputLabel>
          <Select value={repMonth} label="Month" onChange={e => setRepMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel>Year</InputLabel>
          <Select value={repYear} label="Year" onChange={e => setRepYear(Number(e.target.value))}>
            {buildYears().map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>

        {reportType === 'weekly' && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Week</InputLabel>
            <Select value={repWeek} label="Week" onChange={e => setRepWeek(Number(e.target.value))}>
              {weekOptions.map((w: any) => (
                <MenuItem key={w.week} value={w.week}>Week {w.week} ({w.label})</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving || !data}
          sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#00695C' } }}>
          {saving ? 'Saving...' : 'Save Report'}
        </Button>

        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ bgcolor: '#1b5e20', '&:hover': { bgcolor: '#2e7d32' } }}>
          Print / PDF
        </Button>
      </Box>

      {saveMsg && (
        <Alert severity={saveMsg.startsWith('Failed') ? 'error' : 'success'} sx={{ mb: 2, '@media print': { display: 'none' } }}>
          {saveMsg}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2, '@media print': { display: 'none' } }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {/* SLIDE 1 — Cover Page */}
          <CoverPage data={data} />

          {/* SLIDE 2 — Substrate Supply */}
          <SubstrateSupplyPage data={data.substrateSupply} reportData={data} />

          {/* SLIDE 3 — Biogas Conversion Yield (Weekly) */}
          <BiogasConversionWeeklyPage data={data.biogasConversion} reportData={data} />

          {/* SLIDE 4 — Biogas Conversion Yield (Monthly / YTD) */}
          <BiogasConversionMonthlyPage data={data.biogasConversion} reportData={data} />

          {/* SLIDE 5 — Biogas Utilization */}
          <BiogasUtilizationPage data={data.biogasUtilization} reportData={data} />

          {/* SLIDE 6 — Power Generation */}
          <PowerGenerationPage data={data.powerGeneration} reportData={data} />

          {/* SLIDE 7 — Weekly Updates */}
          <WeeklyUpdatesPage data={data.weeklyUpdates} reportData={data} />

          {/* SLIDE 8 — Revenue Tracking */}
          <RevenueTrackingPage data={data.revenueTracking} reportData={data} />
        </>
      ) : null}

      {/* Print global styles */}
      <style>{`
        @media screen {
          .report-page { zoom: 1; margin-bottom: 40px; }
        }
        @media print {
          @page portrait-page  { size: A4 portrait;  margin: 10mm 12mm; }
          @page landscape-page { size: A4 landscape; margin: 8mm 10mm; }

          .portrait-page  { page: portrait-page; }
          .landscape-page { page: landscape-page; }

          .report-page {
            break-after: page;
            zoom: 1 !important;
            margin: 0 !important;
            box-shadow: none !important;
            border: none !important;
            overflow: visible !important;
          }
          .report-page:last-of-type { break-after: avoid; }

          /* Hide sidebar, topbar, nav */
          .MuiDrawer-root, .MuiAppBar-root, header, nav { display: none !important; }

          /* Ensure recharts renders for print */
          .recharts-wrapper { page-break-inside: avoid; }
        }
      `}</style>
    </Box>
  );
};

export default WeeklyMonthlyReport;
